// İSG uzmanı portalı — Kapı kodu katmanı durum motoru (S32 tasarım spec'i §2/§5.2; S32b
// `docs/reviews/S32b-gate-desk-relayout.md` §2 "the verdict is a property of the desk").
//
// Bu hook İKİ şeyi çözer:
//
// 1. **Dönme** — kod sunucunun `expiresAt`'inden hesaplanan ~110 sn'de (120 sn TTL'e karşı)
//    SESSİZCE yenilenir; ölü kod hiç ekranda kalmaz (plan 2.4).
// 2. **Dönme yarışı** (plan 2.9, backend review §9) — tam dönme anında düşen bir okutma ESKİ
//    jetona yazılır. Çözüm: dönmeden sonra ESKİ jeton da (yeniyle BİRLİKTE) yoklanmaya devam
//    eder; sunucu onun `pollAfterSeconds`'ını `null` döndürünce (artık okutma kabul edemiyor —
//    süresi doldu ya da okutma sınırına ulaştı) bırakılır. Birleştirilmiş, `scannedAt`'e göre
//    sıralanmış + (`scannedAt`,`personnelName`) üzerinde tekilleştirilmiş TAZE okutmalar
//    `onScansDiscovered`'a HAM `GateScanDto[]` olarak iletilir.
//
// **S32b'de DEĞİŞEN.** Eşleştirme/sahne/kuyruk (S32 §3) artık BURADA YAŞAMIYOR — Option A'nın
// "Karar" bandı sayfanın TEK verdict yüzeyidir (hem kapı koduyla hem TC ile sorguyla beslenir),
// bu yüzden staging `apps/web-expert/src/lib/useVerdictStage.ts`'e taşındı. Bu hook artık yalnız
// TOKEN/POLLING katmanıdır — `panel`'in `'verdict'` türü YOKTUR, `acknowledge` YOKTUR.
//
// **Okutma sınırı (K8) sessizce ele alınır.** Geçerli jeton `Scanned` + `pollAfterSeconds=null`
// döndürdüğünde (sınıra ulaşmış — §29.2) panel SESSİZCE yenilenir: bu bir güvenlik olayı değil,
// "kapı meşgul" demektir (tasarım spec'i §5.2). `Expired`/`Unknown` ise AKSİNE görünür bir panel
// + elle "Yeni kod üret" gerektirir.

import {
  type ApiError,
  type GateResultDto,
  type GateScanDto,
  isApiError,
  securityApi,
} from '@isgportal/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { securityKeys } from './securityKeys';

/** Dönme, TTL dolmadan ÖNCE tetiklenir (tasarım spec'i §2 — "≈110 sn / 120 sn TTL"). */
const ROTATE_BEFORE_EXPIRY_MS = 10_000;
/** Süre/dönme kontrol tik'i — sunucu yoklama aralığından BAĞIMSIZ, saf UI zamanlayıcısı. */
const TICK_MS = 1000;
/** Son 30 sn'de sayaç gösterilir (tasarım spec'i §2 [P]) — sürekli tik'leyen bir sayı yok. */
const COUNTDOWN_VISIBLE_SECONDS = 30;
/** Sunucu TTL'i (§29.2) — yüzüğün dekoratif kesrini hesaplamak için (`expiresAt - TTL`). */
const TOKEN_TTL_SECONDS = 120;
/**
 * Kod yeniden-üretimi (`issue()`) art arda BAŞARISIZ olursa dönme döngüsünün üstel
 * geri-çekilmesinin tavanı (S32 review #5) — sabit 1 sn'lik yeniden deneme sunucuyu/ağı
 * sonsuza dek döver, üstelik ekrandaki kod bu süre boyunca zaten canlı DEĞİLDİR.
 */
const MAX_ISSUE_RETRY_BACKOFF_MS = 30_000;
/** Yoklama backoff'unun tavanı (S32 review #4) — bkz. `deriveRefetchInterval`. */
const MAX_POLL_RETRY_BACKOFF_MS = 30_000;

interface TokenState {
  qrToken: string;
  /** UTC ISO 8601 (§4) — sunucudan; ROTASYON HESABI YALNIZ BUNA dayanır. */
  expiresAt: string;
}

export type GateLayerPanel =
  | { kind: 'issuing' }
  | { kind: 'error' }
  | { kind: 'forbidden' }
  | { kind: 'tokenExpired' }
  | { kind: 'idle' };

export interface UseGateCodeResult {
  panel: GateLayerPanel;
  qrToken: string | null;
  /** Yalnız son 30 sn'de dolu (tasarım spec'i §2) — aksi hâlde `null`. */
  secondsRemaining: number | null;
  /**
   * Kalan ömrün SÜREKLİ kesri (1 → yeni üretildi, 0 → süresi doldu) — yüzüğün dekoratif
   * animasyonu için (tasarım spec'i §2: "The ring is decoration... not a deadline"). Sayaçtan
   * (son 30 sn) BAĞIMSIZ, her zaman doludur (jeton varken).
   */
  ringProgress: number | null;
  /** Geçerli jetonun yoklaması ağ/sunucu hatası ALMADI mı (403 hariç — o `forbidden`'a düşer). */
  connectionOk: boolean;
  /** Elle yeni kod üretir (`Expired`/`Unknown` panelindeki "Yeni kod üret" düğmesi). */
  reissue: () => void;
}

function isForbidden(err: unknown): boolean {
  return isApiError(err) && err.statusCode === 403 && err.errorCode === 'FORBIDDEN';
}

function isOtherApiError(err: unknown): boolean {
  return isApiError(err) && !isForbidden(err);
}

/** (`scannedAt`,`personnelName`) birleşimi — dönme yarışının merge/dedupe anahtarı (tasarım
 *  spec'i §3). Yalnız bu dosyada kullanılır (S32b'de rayın kalkmasıyla `recentScans.ts` silindi,
 *  `scanKey`'in tek çağıranı burası — dosya başı notu). */
function scanKey(scannedAt: string, personnelName: string): string {
  return `${scannedAt}|${personnelName}`;
}

/**
 * Sunucu güdümlü yoklama aralığı (§1/§20.1 konvansiyonu) — `null` görününce yoklama DURUR.
 *
 * **`security:gate-result` 403'ü (K4) burada AYRICA ele alınır.** Aksiyon SEEDLİ OLARAK
 * yetkisiz gelir, yani her kurulum bunda başlar (tasarım spec'i §8.4 — "every installation
 * starts here"). `retry: false` olduğu için ilk istek bir kez 403 döner ve `data` HİÇBİR ZAMAN
 * dolmaz — bu `error` kontrolü olmadan fonksiyon "ilk yanıt henüz gelmedi" dalına düşer ve 2 sn
 * tabanında SONSUZA dek 403 atar. Yetki, sekme açıkken kendiliğinden BELİRMEZ — bu yüzden
 * geri-çekilme değil, doğrudan DURDURMA doğru olan: panel zaten `isForbidden` üzerinden
 * "forbidden" durumuna geçti (bkz. aşağıdaki `panel` `useMemo`'su), bu yalnız TRAFİĞİ keser.
 *
 * **Diğer KALICI hatalar da aynı sonsuz döngüye düşer (S32 review #4).** `FORBIDDEN` dışındaki
 * her hata — 500, ağ hatası, ya da `errorCode` eşleşmediği için `isForbidden`'ın KASITLI
 * dışarıda bıraktığı `403 COMPANY_NOT_ACCESSIBLE` — üstel geri-çekilme ile yoklamaya DEVAM eder
 * (tasarım spec'i §8.8 "keep the last verdict... and retry"), tavanı `MAX_POLL_RETRY_BACKOFF_MS`.
 */
function deriveRefetchInterval(
  data: GateResultDto | undefined,
  error: ApiError | null,
  failureCount: number,
): number | false {
  if (isForbidden(error)) {
    return false;
  }
  if (data == null) {
    if (error != null) {
      return Math.min(MAX_POLL_RETRY_BACKOFF_MS, 2000 * 2 ** Math.min(failureCount, 4));
    }
    // İlk yanıt henüz gelmedi — KK4'ün 2 sn hedefi (tasarım spec'i §3, "client floor is 2 s").
    return 2000;
  }
  if (data.pollAfterSeconds == null) {
    return false;
  }
  return Math.max(1000, data.pollAfterSeconds * 1000);
}

export function useGateCode(
  siteUuid: string,
  active: boolean,
  onScansDiscovered: (scans: GateScanDto[]) => void,
): UseGateCodeResult {
  const [current, setCurrent] = useState<TokenState | null>(null);
  const [previous, setPrevious] = useState<TokenState | null>(null);
  const currentRef = useRef<TokenState | null>(null);
  const rotatingRef = useRef(false);
  const seenKeys = useRef<Set<string>>(new Set());
  // Art arda başarısız `issue()` çağrılarının SAYACI + son deneme zamanı (S32 review #5) —
  // dönme aralığının 1 sn'lik tik'i bunları okuyarak geri-çekilme uygular, bkz. aşağıdaki
  // "Zaman-güdümlü dönme" efekti.
  const issueFailuresRef = useRef(0);
  const lastIssueAttemptRef = useRef(0);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  const createMutation = useMutation({
    mutationFn: () => securityApi.createGateQr({ siteUuid }),
  });

  const issue = useCallback(() => {
    if (rotatingRef.current) {
      return;
    }
    rotatingRef.current = true;
    lastIssueAttemptRef.current = Date.now();
    createMutation.mutate(undefined, {
      onSuccess: (token) => {
        issueFailuresRef.current = 0;
        const outgoing = currentRef.current;
        if (outgoing) {
          setPrevious(outgoing);
        }
        setCurrent({ qrToken: token.qrToken, expiresAt: token.expiresAt });
      },
      onError: () => {
        issueFailuresRef.current += 1;
      },
      onSettled: () => {
        rotatingRef.current = false;
      },
    });
    // `siteUuid` kasıtlı olarak bağımlılık DEĞİL: `issue` onu doğrudan kullanmıyor, yalnız
    // `createMutation.mutate`'i çağırıyor — react-query o çağrının `mutationFn`'ini her render'da
    // en güncel `siteUuid`'i kapsayacak şekilde tazeler (`observer.setOptions`), `mutate`'in
    // kendisi referans olarak sabittir.
  }, [createMutation.mutate]);

  // Katman açıldığında ilk kod; kapandığında her şey unutulur (yeniden girişte YENİ kod —
  // önceki jeton ASLA diriltilmez, tasarım spec'i §2 "Exit").
  useEffect(() => {
    if (active) {
      if (!currentRef.current && !rotatingRef.current) {
        issue();
      }
      return;
    }
    setCurrent(null);
    setPrevious(null);
    seenKeys.current = new Set();
  }, [active, issue]);

  const currentQuery = useQuery<GateResultDto, ApiError>({
    queryKey: securityKeys.gateResult(current?.qrToken ?? ''),
    queryFn: () => securityApi.gateResult({ qrToken: current?.qrToken as string }),
    enabled: active && current != null,
    retry: false,
    refetchInterval: (q) =>
      deriveRefetchInterval(q.state.data, q.state.error, q.state.fetchFailureCount),
  });

  const previousQuery = useQuery<GateResultDto, ApiError>({
    queryKey: securityKeys.gateResult(previous?.qrToken ?? ''),
    queryFn: () => securityApi.gateResult({ qrToken: previous?.qrToken as string }),
    enabled: active && previous != null,
    retry: false,
    refetchInterval: (q) =>
      deriveRefetchInterval(q.state.data, q.state.error, q.state.fetchFailureCount),
  });

  // Önceki jeton artık yeni okutma kabul edemiyor (`pollAfterSeconds=null`) → dönme yarışı
  // penceresi kapandı, bırak (dosya üstü notu — sunucu-güdümlü "bir tur daha").
  useEffect(() => {
    if (previous && previousQuery.data && previousQuery.data.pollAfterSeconds == null) {
      setPrevious(null);
    }
  }, [previous, previousQuery.data]);

  // Zaman-güdümlü dönme — sunucunun `expiresAt`'i TEK kaynak (yerel bir başlangıç zamanı DEĞİL).
  //
  // **Üstel geri-çekilme (S32 review #5).** `issue()` art arda BAŞARISIZ olursa (`createGateQr`
  // sürekli hata veriyor), bu tik her 1 sn'de bir yeniden dener — geri-çekilme olmadan bu, ağı
  // sonsuza dek 1 sn'de bir döven bir döngüdür. `issueFailuresRef` sıfırsa (sağlıklı durum)
  // davranış AYNI kalır: her tik'te dener.
  useEffect(() => {
    if (!active || !current) {
      return;
    }
    const id = setInterval(() => {
      const remainingMs = new Date(current.expiresAt).getTime() - Date.now();
      if (remainingMs > ROTATE_BEFORE_EXPIRY_MS) {
        return;
      }
      if (issueFailuresRef.current > 0) {
        const backoffMs = Math.min(
          MAX_ISSUE_RETRY_BACKOFF_MS,
          1000 * 2 ** Math.min(issueFailuresRef.current, 5),
        );
        if (Date.now() - lastIssueAttemptRef.current < backoffMs) {
          return;
        }
      }
      issue();
    }, TICK_MS);
    return () => clearInterval(id);
  }, [active, current, issue]);

  // Okutma sınırına ulaşmış geçerli jeton → SESSİZCE yenile (K8 — "güvenlik olayı değil").
  useEffect(() => {
    if (
      active &&
      current &&
      currentQuery.data?.status === 'Scanned' &&
      currentQuery.data.pollAfterSeconds == null
    ) {
      issue();
    }
  }, [active, current, currentQuery.data, issue]);

  // Kalan saniye (yalnız son 30 sn'de dolu, §2 [P]) + yüzüğün sürekli kesri (dekoratif).
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [ringProgress, setRingProgress] = useState<number | null>(null);
  // Yerel saate göre ölü kod (S32 review #5) — sunucunun `Expired`/`Unknown` durumunu
  // BEKLEMEDEN: `issue()` art arda başarısız olursa (ör. bağlantı tamamen kesikse) sunucu bu
  // durumu HİÇ doğrulayamayabilir, ama kodun kendi 120 sn TTL'i zaten geçmiştir. `panel`
  // `useMemo`'su bunu OR'layarak canlı-görünen ölü kodu asla sahnede TUTMAZ.
  const [localExpired, setLocalExpired] = useState(false);
  useEffect(() => {
    if (!active || !current) {
      setSecondsRemaining(null);
      setRingProgress(null);
      setLocalExpired(false);
      return;
    }
    const expiresAtMs = new Date(current.expiresAt).getTime();
    function tick() {
      const rawRemainingMs = expiresAtMs - Date.now();
      const remainingMs = Math.max(0, rawRemainingMs);
      const remaining = Math.round(remainingMs / 1000);
      setSecondsRemaining(remaining <= COUNTDOWN_VISIBLE_SECONDS ? remaining : null);
      setRingProgress(Math.min(1, remainingMs / (TOKEN_TTL_SECONDS * 1000)));
      setLocalExpired(rawRemainingMs <= 0);
    }
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [active, current]);

  // ---- Birleştirme + dönme yarışı tekilleştirmesi (tasarım spec'i §3) ----
  //
  // S32b'de sahne/kuyruk YOKTUR (dosya başı notu) — bu efek yalnız TAZE (daha önce görülmemiş)
  // okutmaları `scannedAt`'e göre sıralar, tekilleştirir ve HAM `GateScanDto[]` olarak
  // `onScansDiscovered`'a iletir; hangi okutmanın sahneye çıkacağına `useVerdictStage` karar verir.
  useEffect(() => {
    const merged = new Map<string, GateScanDto>();
    for (const scan of currentQuery.data?.scans ?? []) {
      merged.set(scanKey(scan.scannedAt, scan.personnelName), scan);
    }
    for (const scan of previousQuery.data?.scans ?? []) {
      const key = scanKey(scan.scannedAt, scan.personnelName);
      if (!merged.has(key)) {
        merged.set(key, scan);
      }
    }
    const fresh = [...merged.entries()]
      .filter(([key]) => !seenKeys.current.has(key))
      .sort((a, b) => a[1].scannedAt.localeCompare(b[1].scannedAt));

    if (fresh.length === 0) {
      return;
    }
    for (const [key] of fresh) {
      seenKeys.current.add(key);
    }
    onScansDiscovered(fresh.map(([, scan]) => scan));
  }, [currentQuery.data, previousQuery.data, onScansDiscovered]);

  // ---- Bağlantı sağlığı — yalnız GEÇERLİ jetonun yoklaması (403 hariç: o ayrı bir durumdur) ----
  const connectionOk = !isOtherApiError(currentQuery.error);

  const panel: GateLayerPanel = useMemo(() => {
    if (isForbidden(createMutation.error) || isForbidden(currentQuery.error)) {
      return { kind: 'forbidden' };
    }
    if (!current) {
      if (createMutation.isError) {
        return { kind: 'error' };
      }
      return { kind: 'issuing' };
    }
    const status = currentQuery.data?.status;
    // `localExpired` OR'lanır (S32 review #5): sunucunun `Expired`/`Unknown` durumu HİÇ
    // gelmeyebilir (ör. ağ tamamen kesikse), ama kod kendi TTL'ini zaten geçtiyse canlı GİBİ
    // gösterilmemeli — ölü bir kodu okutan işçi hak etmediği bir ret alır.
    if (status === 'Expired' || status === 'Unknown' || localExpired) {
      return { kind: 'tokenExpired' };
    }
    return { kind: 'idle' };
  }, [
    createMutation.error,
    createMutation.isError,
    currentQuery.error,
    currentQuery.data?.status,
    current,
    localExpired,
  ]);

  return {
    panel,
    qrToken: current?.qrToken ?? null,
    secondsRemaining,
    ringProgress,
    connectionOk,
    reissue: issue,
  };
}
